/* ============================================================
 *  Bundle Builder (Apple-style) — split layout v2
 *  Custom element: <mod-bundle-builder>
 * ============================================================ */

(function () {
  if (customElements.get('mod-bundle-builder')) return;

  /* ---------- helpers ---------- */
  // Shopify stores ALL prices as cents (smallest unit × 100), even zero-decimal currencies like VND.
  // Liquid `money` filter handles divide-by-100 + formatting. In JS we replicate by /100 + Intl.
  const formatMoney = (cents, currency) => {
    if (window.Shopify && window.Shopify.formatMoney && window.theme && window.theme.moneyFormat) {
      try {
        return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
      } catch (_) {}
    }
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'vi-VN', {
        style: 'currency',
        currency: currency || 'VND',
      }).format((cents || 0) / 100);
    } catch (_) {
      return `${Math.round((cents || 0) / 100).toLocaleString()} ${currency || ''}`;
    }
  };

  const escapeHtml = (str) =>
    String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  /* ---------- main class ---------- */
  class BundleBuilderApple extends HTMLElement {
    constructor() {
      super();
      this.selections = new Map(); // stepIndex -> selection object (single-select steps)
      // Multi-select steps store their picks in a separate Map keyed by productId
      // so each step can hold N concurrent selections without colliding with the
      // single-select code paths that read/write `this.selections` directly.
      this.multiSelections = new Map(); // stepIndex -> Map<productId, selectionObj>
      this.skipped = new Set();
      // stepIndex -> { byProductId, byProductTitle, byStepIndex } — populated when a selected
      // product declares (via custom.bundle_includes metafield) that it already covers a step
      this.autoSkipped = new Map();
      this.steps = [];
      this.currency = this.dataset.currency || 'VND';
      this.bundleKey = this.dataset.bundleKey || 'bundle';
      this.isSubmitting = false;
    }

    connectedCallback() {
      this.parseData();
      this.cacheRefs();
      this.markSingleProductSteps();
      this.bindEvents();
      this.setupScrollSpy();
      this.setupStickyGallery();
      this.setupSelectedLabels();
      this.autoSelectSingleRequired();
      this.updateUI();
      this.preloadGalleryImages();
    }

    disconnectedCallback() {
      if (this._stickyResizeHandler) {
        window.removeEventListener('resize', this._stickyResizeHandler);
      }
      if (this._stickyResizeObs) {
        this._stickyResizeObs.disconnect();
      }
    }

    /* ---------- Sticky gallery: 3-phase scroll behavior (top → centered → bottom) ----------
       The gallery's left column uses position: sticky with a JS-computed `top` value so it:
         1. starts top-aligned with the right column at section start,
         2. centers vertically in the visible area while scrolling through the middle,
         3. bottom-aligns with the right column when the section's bottom comes into view.

       Math: centeredTop = headerH + max(0, (viewportH - headerH - galleryH) / 2).
       We push this onto --bba-sticky-top and let CSS sticky do the rest (the parent .bba-shell
       acts as the sticky boundary, which gives us the bottom-alignment phase for free). */
    setupStickyGallery() {
      this._stickyMediaCol = this.querySelector('.bba-global-media-col');
      if (!this._stickyMediaCol) return;

      this._updateStickyTop = () => {
        // Mobile (< 900px) — sticky CSS is disabled, no need to set the var
        if (window.innerWidth < 900) {
          this._stickyMediaCol.style.removeProperty('--bba-sticky-top');
          return;
        }
        const galleryH = this._stickyMediaCol.offsetHeight;
        if (!galleryH) return; // not yet laid out
        const viewportH = window.innerHeight;
        const headerH = this._getStickyHeaderHeight();
        const visibleH = Math.max(0, viewportH - headerH);
        // Center the gallery in the visible viewport area below the header. If the gallery
        // is taller than the visible area, just pin it to the header (no negative offset).
        const centerOffset = Math.max(0, (visibleH - galleryH) / 2);
        const centeredTop = headerH + centerOffset;
        this._stickyMediaCol.style.setProperty('--bba-sticky-top', `${centeredTop}px`);
      };

      this._updateStickyTop();

      this._stickyResizeHandler = () => this._updateStickyTop();
      window.addEventListener('resize', this._stickyResizeHandler);

      // Recompute when the visible gallery wrap changes height (different product → different
      // aspect ratio → different gallery height).
      if ('ResizeObserver' in window) {
        this._stickyResizeObs = new ResizeObserver(() => this._updateStickyTop());
        this._stickyResizeObs.observe(this._stickyMediaCol);
      }
    }

    /* Measure exact pixel widths of "Đã chọn" / "Chi tiết sản phẩm" so the
       hover-expanding label hugs each text snugly (no dead space). All cards
       share the same texts + font, so we only measure the first badge and
       publish the values as CSS vars on the section host — every label inside
       inherits them. Width animation interpolates between exact pixels →
       smooth + tight. */
    setupSelectedLabels() {
      const sample = this.querySelector('.bba-row__selected-badge');
      if (!sample) return;
      const defText = sample.querySelector('.bba-row__selected-text--default');
      const hoverText = sample.querySelector('.bba-row__selected-text--hover');
      if (!defText || !hoverText) return;

      // Wait one frame so layout/fonts are settled before measuring.
      requestAnimationFrame(() => {
        // Both spans are position:absolute with white-space:nowrap, so offsetWidth
        // reflects the natural content width regardless of the parent's clipped
        // size or display state.
        const defW = defText.offsetWidth;
        const hoverW = hoverText.offsetWidth;
        if (defW > 0 && hoverW > 0) {
          this.style.setProperty('--bba-label-w-default', `${defW}px`);
          this.style.setProperty('--bba-label-w-hover', `${hoverW}px`);
        }
      });
    }

    _getStickyHeaderHeight() {
      // Theme runtime sets --header-height on the documentElement. Read it directly to stay
      // in sync with the actual sticky header (handles announcement bar, etc.).
      const cssVal = getComputedStyle(document.documentElement)
        .getPropertyValue('--header-height')
        .trim();
      const parsed = parseFloat(cssVal);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 80;
    }

    /* Preload all product gallery images so switching between products is instant.
       Without this, the hidden .bba-gallery-wrap (display:none) defers image loads
       and users see a blank frame while the image fetches on first click.

       Variant featuredImage ships at width=800 in the JSON (sized for the big
       desktop gallery). The card thumbnail swap (_swapCardThumbnail) requests
       width=200 — a different URL string → different cache entry → cache miss
       on first interaction. We preload BOTH widths so mobile variant picks feel
       instant regardless of which surface (big gallery vs card thumbnail)
       consumes the image. */
    preloadGalleryImages() {
      if (!this.steps || !this.steps.length) return;
      const schedule = window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
      schedule(() => {
        const seenProducts = new Set();
        const urls = [];
        this.steps.forEach((step) => {
          (step.products || []).forEach((product) => {
            if (seenProducts.has(product.id)) return;
            seenProducts.add(product.id);
            // Featured image first (the one shown by default) — highest priority
            if (product.featuredImage) urls.push(product.featuredImage);
            // Then every other media frame for this product
            (product.media || []).forEach((m) => {
              if (m && m.url) urls.push(m.url);
            });
            // Variant-specific images (e.g., color swatches that swap the main image)
            (product.variants || []).forEach((v) => {
              if (v && v.featuredImage) {
                urls.push(v.featuredImage);
                // Also preload at the thumbnail's responsive widths so the card
                // swap on variant change hits the HTTP cache instead of fetching.
                urls.push(this._resizeShopifyImageUrl(v.featuredImage, 200));
                urls.push(this._resizeShopifyImageUrl(v.featuredImage, 150));
                urls.push(this._resizeShopifyImageUrl(v.featuredImage, 100));
              }
            });
          });
        });
        // Dedupe URLs and trigger background fetch into the browser cache
        Array.from(new Set(urls)).forEach((src) => {
          const img = new Image();
          img.decoding = 'async';
          img.src = src;
        });
      });
    }

    /* Rewrite a Shopify CDN URL's `width=` query param. Same helper used by
       both the preloader and _swapCardThumbnail so the two surfaces always
       request identical URLs → guaranteed HTTP cache hit on swap. */
    _resizeShopifyImageUrl(url, width) {
      if (/[?&]width=\d+/.test(url)) {
        return url.replace(/([?&])width=\d+/, `$1width=${width}`);
      }
      const sep = url.includes('?') ? '&' : '?';
      return `${url}${sep}width=${width}`;
    }

    /* ---------- Scroll-spy: dot click jumps to step, IntersectionObserver highlights active ---------- */
    setupScrollSpy() {
      const spyEl = this.querySelector('.bba-scroll-spy');
      const dots = this.querySelectorAll('[data-bba-spy]');
      if (!dots.length) return;

      // Click → scroll to corresponding step
      dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const key = dot.dataset.stepIndex;
          const target = this.querySelector(`.bba-step[data-step-index="${key}"]`);
          if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });

      if (!('IntersectionObserver' in window)) return;

      // Show/hide spy: only visible when section overlaps bottom half of viewport
      // (i.e., section's bottom hasn't passed viewport center yet)
      if (spyEl) {
        const visibilityObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              spyEl.classList.toggle('is-visible', entry.isIntersecting);
            });
          },
          { rootMargin: '-50% 0px 0px 0px', threshold: 0 }
        );
        visibilityObserver.observe(this);
      }

      // Highlight active step based on scroll position
      const setActive = (key) => {
        dots.forEach((d) => {
          d.classList.toggle('is-active', d.dataset.stepIndex === String(key));
        });
      };

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.target.dataset.stepIndex != null) {
              const idx = parseInt(entry.target.dataset.stepIndex, 10);
              setActive(entry.target.dataset.stepIndex);
              // Scroll-driven gallery preview: when a step enters the trigger zone,
              // show that step's selected product (or first product if none picked yet).
              // This works alongside the click-to-select flow without overwriting selections.
              if (Number.isFinite(idx)) this.autoShowGalleryForStep(idx);
            }
          });
        },
        { rootMargin: '-40% 0px -50% 0px', threshold: 0 }
      );

      this.stepEls.forEach((step) => observer.observe(step));
    }

    /* Show the left gallery that matches the step the user just scrolled into. Prefers the
       user's existing selection for that step; otherwise falls back to the step's first
       product so users always see a preview as they browse. Skips the DOM swap when the
       correct wrap is already visible. */
    autoShowGalleryForStep(stepIndex) {
      if (!this.galleryWraps || !this.galleryWraps.length) return;

      // Gallery shows one product at a time — pick the FIRST selection in the
      // step (covers both single-step and multi-step). Single-step always has
      // 0 or 1; multi-step uses insertion order so this lands on whichever
      // card the user picked first this session.
      const sels = this.getStepSelectionObjs(stepIndex);
      let productId = sels[0]?.productId;

      if (!productId) {
        const step = this.steps && this.steps[stepIndex];
        const firstProduct = step && step.products && step.products[0];
        if (firstProduct) productId = firstProduct.id;
      }
      if (productId == null) return;

      const target = String(productId);
      const visible = Array.from(this.galleryWraps).find((w) => !w.hidden);
      if (visible && visible.dataset.productId === target) return;

      this.showProductInGallery(stepIndex, productId, null, null);
    }

    /* ---------- single-product steps: hide selection ring, auto-select if required ---------- */
    markSingleProductSteps() {
      this.stepEls.forEach((stepEl) => {
        const cards = stepEl.querySelectorAll('[data-bba-card]');
        if (cards.length === 1) {
          stepEl.classList.add('bba-step--single');
        }
      });
    }

    autoSelectSingleRequired() {
      this.stepEls.forEach((stepEl) => {
        const isRequired = stepEl.dataset.stepRequired === 'true';
        const cards = stepEl.querySelectorAll('[data-bba-card]');
        if (cards.length === 1 && isRequired) {
          const card = cards[0];
          if (!card.classList.contains('is-selected')) {
            // silent: don't hijack the gallery — keep it on the first block's first product
            // until the user actually clicks something
            this.toggleCard(card, { silent: true });
          }
        }
      });
    }

    /* ---------- setup ---------- */
    parseData() {
      const scriptEl = this.querySelector('script[data-bba-products]');
      if (!scriptEl) return;
      try {
        this.steps = JSON.parse(scriptEl.textContent.trim());
      } catch (e) {
        console.warn('[mod-bundle-builder] Failed to parse product data', e);
        this.steps = [];
      }
    }

    cacheRefs() {
      this.galleryWraps = this.querySelectorAll('.bba-gallery-wrap');
      this.totalEl = this.querySelector('[data-bba-total]');
      this.submitBtn = this.querySelector('[data-bba-submit]');
      this.submitHint = this.querySelector('[data-bba-submit-hint]');
      this.summaryWrap = this.querySelector('[data-bba-summary]');
      this.summaryList = this.querySelector('[data-bba-summary-list]');
      this.cards = this.querySelectorAll('[data-bba-card]');
      this.skipCards = this.querySelectorAll('[data-bba-skip]');
      this.stepEls = this.querySelectorAll('.bba-step');
    }

    showProductInGallery(stepIndex, productId, imageUrl, mediaId) {
      if (!this.galleryWraps || !this.galleryWraps.length) return;
      const target = String(productId);
      let activeWrap = null;

      this.galleryWraps.forEach((wrap) => {
        const isMatch = wrap.dataset.productId === target;
        wrap.hidden = !isMatch;
        if (isMatch) activeWrap = wrap;
      });

      if (!activeWrap || mediaId == null) return;

      // Wait one frame so the wrap is fully laid out before telling the carousel to scroll.
      // (ScrollCarousel.select() bails early if offsetParent is null / scroll direction unknown.)
      requestAnimationFrame(() => {
        const carousel = activeWrap.querySelector('media-carousel');
        if (!carousel) return;

        const slides = Array.from(carousel.querySelectorAll('.product-gallery__media'));
        const idx = slides.findIndex((el) => el.getAttribute('data-media-id') === String(mediaId));
        if (idx < 0) return;

        try {
          // animate:false so swap is instant; force:true so it scrolls even if index matches
          if (typeof carousel.select === 'function') {
            carousel.select(idx, { animate: false, force: true });
          }
        } catch (_) {}

        // Sync `is-selected` class on slides (in case force was a no-op above)
        slides.forEach((slide, i) => slide.classList.toggle('is-selected', i === idx));

        // Sync the thumbnail/page-dots aria-current
        activeWrap.querySelectorAll('page-dots').forEach((dots) => {
          if (typeof dots.select === 'function') {
            try { dots.select(idx, false); } catch (_) {}
          }
        });
      });
    }

    /* ---------- events ---------- */
    bindEvents() {
      this.cards.forEach((card) => {
        // Click anywhere on the row toggles selection — only skip clicks on actual interactive
        // variant elements (radio inputs + their labels) and the "Đã chọn" link (which navigates
        // to the product page in a new tab, marked with data-bba-skip-card-click).
        card.addEventListener('click', (e) => {
          if (e.target.closest('input, label, [data-bba-skip-card-click]')) return;
          // Block selection on sold-out cards. The CSS dims .bba-row__hit and
          // sets pointer-events: none there, but the article wrapper itself is
          // still click-receiving — without this guard, clicking the row
          // BORDER (outside __hit) still toggled selection and a sold-out
          // variant ended up in the bundle. Variant pickers + the detail link
          // stay reachable via the existing `closest('input,label,…')` skip.
          if (card.dataset.available === 'false') return;
          e.preventDefault();
          this.toggleCard(card);
        });

        // Variant option change
        card.querySelectorAll('[data-bba-option]').forEach((input) => {
          input.addEventListener('change', () => this.handleOptionChange(card));
        });

        // First hover on the "Đã chọn" badge commits the prompt-detail state
        // permanently so the badge doesn't bounce back to "Đã chọn" when the
        // user moves the cursor away. Without this, mouseleave reverts the
        // :hover styles and then the 2s timer fires later → visible flicker
        // through Đã chọn → Chi tiết → Đã chọn → Chi tiết.
        const badge = card.querySelector('.bba-row__selected-badge');
        if (badge) {
          badge.addEventListener('mouseenter', () => {
            if (card.classList.contains('is-selected')) {
              this._promptDetail(card);
            }
          });
        }
      });

      // Skip
      this.skipCards.forEach((skip) => {
        skip.addEventListener('click', () => {
          const idx = parseInt(skip.dataset.stepIndex, 10);
          this.toggleSkip(idx, skip);
        });
      });

      // Submit
      this.submitBtn?.addEventListener('click', () => this.submit());
    }

    /* ---------- bundle auto-skip helpers ---------- */
    findStepIndexByLabel(label) {
      if (!label) return -1;
      // Vietnamese strings can be stored as NFC or NFD — normalize both sides so accents match.
      const norm = (s) => String(s || '').normalize('NFC').trim().toLowerCase();
      const target = norm(label);
      return this.steps.findIndex((s) => norm(s.label) === target);
    }

    /* When this step's selection changes, drop any auto-skip entries it had registered. */
    clearBundleAutoSkipFromStep(stepIndex) {
      for (const [skippedIdx, info] of this.autoSkipped.entries()) {
        if (info.byStepIndex === stepIndex) {
          this.autoSkipped.delete(skippedIdx);
        }
      }
    }

    /* Register auto-skips for every step listed in product.bundleIncludes (a list of step labels). */
    applyBundleAutoSkip(stepIndex, product) {
      if (!product || !Array.isArray(product.bundleIncludes) || product.bundleIncludes.length === 0) return;
      product.bundleIncludes.forEach((label) => {
        const targetIdx = this.findStepIndexByLabel(label);
        if (targetIdx === -1 || targetIdx === stepIndex) return;
        this.autoSkipped.set(targetIdx, {
          byStepIndex: stepIndex,
          byProductTitle: product.shortTitle || product.title || '',
        });
        // Drop the user's prior choice in the now-bypassed step
        this.selections.delete(targetIdx);
        this.skipped.delete(targetIdx);
        this.getCardsInStep(targetIdx).forEach((c) => {
          c.classList.remove('is-selected');
          c.querySelector('[data-bba-card-select]')?.setAttribute('aria-pressed', 'false');
        });
        this.getSkipCardForStep(targetIdx)?.classList.remove('is-selected');
      });
    }

    /* ---------- "Đã chọn" badge auto-prompt helpers ----------
       Three operations on the prompt-detail state per card:
         _schedulePromptTimer — start the 2s "wait then show Chi tiết sản phẩm" countdown
         _promptDetail        — flip to the detail state NOW (called on first hover)
         _resetPromptDetail   — clear both timer and class (deselect / sibling pick)

       Splitting "cancel timer" from "remove class" fixes the desktop bounce bug:
       previously, hovering the badge showed the dark "Chi tiết" state via :hover,
       but on mouseleave it snapped back to "Đã chọn" because no class had been
       applied yet — then 2s later the timer fired and flipped it again. Now the
       first hover commits the state permanently. */
    _schedulePromptTimer(card) {
      this._cancelPromptTimer(card);
      card._bbaPromptTimer = setTimeout(() => {
        card.classList.add('is-prompt-detail');
        card._bbaPromptTimer = null;
      }, 2000);
    }

    _cancelPromptTimer(card) {
      if (card._bbaPromptTimer) {
        clearTimeout(card._bbaPromptTimer);
        card._bbaPromptTimer = null;
      }
    }

    _promptDetail(card) {
      this._cancelPromptTimer(card);
      card.classList.add('is-prompt-detail');
    }

    _resetPromptDetail(card) {
      this._cancelPromptTimer(card);
      card.classList.remove('is-prompt-detail');
    }

    /* ---------- card selection ----------
       Single-select steps (`step`, `addon_step`): clicking a card REPLACES the
       step's selection (deselects siblings). Multi-select steps (`multi_step`):
       each click TOGGLES that specific card; siblings stay as-is. The two
       flows are split into different methods so the single-select code path
       remains untouched. */
    toggleCard(card, { silent = false } = {}) {
      const stepIndex = parseInt(card.dataset.stepIndex, 10);
      if (this.isMultiStep(stepIndex)) {
        return this.toggleMultiCard(card);
      }
      const productId = card.dataset.productId;
      const isSelected = card.classList.contains('is-selected');

      if (isSelected) {
        this.selections.delete(stepIndex);
        card.classList.remove('is-selected');
        this._resetPromptDetail(card);
        card.querySelector('[data-bba-card-select]')?.setAttribute('aria-pressed', 'false');
        this.clearBundleAutoSkipFromStep(stepIndex);
      } else {
        // Deselect siblings — also clear their auto-prompt timer + .is-prompt-detail class
        // so the badge resets cleanly if the user picks a different product before the 2s.
        this.getCardsInStep(stepIndex).forEach((sibling) => {
          if (sibling !== card) {
            sibling.classList.remove('is-selected');
            this._resetPromptDetail(sibling);
            sibling.querySelector('[data-bba-card-select]')?.setAttribute('aria-pressed', 'false');
          }
        });
        // Clear skip
        this.skipped.delete(stepIndex);
        this.getSkipCardForStep(stepIndex)?.classList.remove('is-selected');

        card.classList.add('is-selected');
        card.querySelector('[data-bba-card-select]')?.setAttribute('aria-pressed', 'true');

        // After 2s, nudge the "Đã chọn" badge into its hover state ("Chi tiết
        // sản phẩm" + dark fill) so users notice they can also click through
        // to the product page. Cleared if they deselect / switch product first.
        if (!silent) {
          this._schedulePromptTimer(card);
        }

        const variantId = parseInt(card.dataset.variantId, 10);
        const variant = this.findVariant(stepIndex, productId, variantId);
        const product = this.findProduct(stepIndex, productId);

        // Clear prior auto-skips this step registered, then register new ones based on the chosen product
        this.clearBundleAutoSkipFromStep(stepIndex);

        this.selections.set(stepIndex, {
          stepIndex,
          stepLabel: this.getStepLabel(stepIndex),
          productId,
          productHandle: card.dataset.productHandle,
          productTitle: product?.shortTitle || product?.title || '',
          productCategory: product?.category || '',
          variantId,
          variantTitle: variant?.title === 'Default Title' ? '' : (variant?.title || ''),
          price: variant?.price ?? parseInt(card.dataset.price, 10),
          image: variant?.featuredImage || product?.featuredImage || null,
          quantity: 1,
        });

        this.applyBundleAutoSkip(stepIndex, product);

        // Update gallery to show this product (and jump to the variant's media if available).
        // Skipped when called silently (e.g. auto-select on page load) so the gallery stays
        // anchored on the first block's first product until the user clicks something.
        if (!silent) {
          this.showProductInGallery(stepIndex, productId, variant?.featuredImage, variant?.featuredMediaId);
        }
      }

      this.updateUI();
    }

    /* Multi-select toggle: add this card to the step's multi-Map, OR remove
       if already there. Does NOT touch siblings. Caps the step at
       step.maxItems — clicking a fresh card after the cap is a no-op. */
    toggleMultiCard(card) {
      const stepIndex = parseInt(card.dataset.stepIndex, 10);
      const productId = card.dataset.productId;
      const step = this.steps[stepIndex];
      if (!step) return;

      let stepMap = this.multiSelections.get(stepIndex);
      if (!stepMap) {
        stepMap = new Map();
        this.multiSelections.set(stepIndex, stepMap);
      }

      if (stepMap.has(productId)) {
        // Already selected → remove
        stepMap.delete(productId);
        card.classList.remove('is-selected');
        card.querySelector('[data-bba-card-select]')?.setAttribute('aria-pressed', 'false');
        this._resetPromptDetail(card);
      } else {
        // New selection — guard against max-items overflow
        const maxItems = step.maxItems || 10;
        if (stepMap.size >= maxItems) return;

        // First selection in this step clears the "Bỏ qua" skip state — same
        // mirror as the single-select path.
        this.skipped.delete(stepIndex);
        this.getSkipCardForStep(stepIndex)?.classList.remove('is-selected');

        const variantId = parseInt(card.dataset.variantId, 10);
        const variant = this.findVariant(stepIndex, productId, variantId);
        const product = this.findProduct(stepIndex, productId);

        stepMap.set(productId, {
          stepIndex,
          stepLabel: this.getStepLabel(stepIndex),
          productId,
          productHandle: card.dataset.productHandle,
          productTitle: product?.shortTitle || product?.title || '',
          productCategory: product?.category || '',
          variantId,
          variantTitle: variant?.title === 'Default Title' ? '' : (variant?.title || ''),
          price: variant?.price ?? parseInt(card.dataset.price, 10),
          image: variant?.featuredImage || product?.featuredImage || null,
          quantity: 1,
        });

        card.classList.add('is-selected');
        card.querySelector('[data-bba-card-select]')?.setAttribute('aria-pressed', 'true');
        // Same 2-second "Đã chọn → Chi tiết sản phẩm" auto-prompt the
        // single-select path uses, so the badge nudges users toward the
        // PDP link on multi-step cards too.
        this._schedulePromptTimer(card);
      }

      this.updateUI();
    }

    toggleSkip(stepIndex, skipCardEl) {
      if (this.skipped.has(stepIndex)) {
        this.skipped.delete(stepIndex);
        skipCardEl.classList.remove('is-selected');
      } else {
        // Deselect any product card
        this.getCardsInStep(stepIndex).forEach((c) => {
          c.classList.remove('is-selected');
          c.querySelector('[data-bba-card-select]')?.setAttribute('aria-pressed', 'false');
        });
        this.selections.delete(stepIndex);
        // Multi-select steps also clear their picks when the user skips
        this.multiSelections.delete(stepIndex);
        this.clearBundleAutoSkipFromStep(stepIndex);
        this.skipped.add(stepIndex);
        skipCardEl.classList.add('is-selected');
      }
      this.updateUI();
    }

    /* ---------- variant logic ---------- */
    handleOptionChange(card) {
      const productId = card.dataset.productId;
      const stepIndex = parseInt(card.dataset.stepIndex, 10);
      const product = this.findProduct(stepIndex, productId);
      if (!product) return;

      const inputs = card.querySelectorAll('[data-bba-option]');
      const selectedValues = [];
      inputs.forEach((input) => {
        if (input.checked) {
          const pos = parseInt(input.dataset.optionPosition, 10);
          selectedValues[pos - 1] = input.value;
        }
      });

      const matched = product.variants.find((v) =>
        v.options.every((opt, i) => opt === selectedValues[i])
      );

      if (matched) {
        card.dataset.variantId = matched.id;
        card.dataset.price = matched.price;
        card.dataset.available = matched.available;
        card.classList.toggle('bba-row--sold-out', !matched.available);

        const priceEl = card.querySelector('[data-bba-card-price]');
        if (priceEl) priceEl.textContent = matched.priceFormatted || formatMoney(matched.price, this.currency);

        // Block-swatch labels (size/material): toggle is-selected to keep state in sync with input
        card.querySelectorAll('.block-swatch').forEach((label) => {
          const forId = label.getAttribute('for');
          if (!forId) return;
          const input = card.querySelector(`#${CSS.escape(forId)}`);
          label.classList.toggle('is-selected', !!input?.checked);
        });

        // Update dropdown trigger text (for non-color options rendered as select)
        card.querySelectorAll('[data-bba-select-trigger]').forEach((trigger) => {
          const pos = trigger.dataset.optionPosition;
          const checked = card.querySelector(
            `input[data-bba-option][data-option-position="${pos}"]:checked`
          );
          if (checked) {
            const valueEl = trigger.querySelector('[data-bba-select-value]');
            if (valueEl) valueEl.textContent = checked.value;
          }
        });

        // Update selected color name display
        card.querySelectorAll('[data-bba-color-name]').forEach((nameEl) => {
          // Find the fieldset this name belongs to
          const fieldset = nameEl.closest('.bba-row__opt');
          if (!fieldset) return;
          const checkedColorInput = fieldset.querySelector('input[data-bba-option]:checked[data-color-name]');
          if (checkedColorInput) {
            nameEl.textContent = checkedColorInput.dataset.colorName;
          }
        });

        // Update gallery: jump to variant's linked media if any
        if (matched.featuredMediaId != null || matched.featuredImage) {
          this.showProductInGallery(stepIndex, productId, matched.featuredImage, matched.featuredMediaId);
        }

        // Swap the card's thumbnail to the variant's featured image. Critical on
        // mobile where the sticky left gallery is hidden — this thumbnail is the
        // ONLY visual representation of the chosen variant. Also keeps desktop
        // cards in sync (the big gallery handles the main view).
        if (matched.featuredImage) {
          this._swapCardThumbnail(card, matched.featuredImage);
        }

        if (card.classList.contains('is-selected')) {
          // If the new variant is sold out, drop the card from the bundle —
          // we never want a sold-out variant in `selections`. toggleCard
          // routes to toggleMultiCard automatically for multi steps.
          if (!matched.available) {
            this.toggleCard(card);
          } else if (this.isMultiStep(stepIndex)) {
            // Multi step: update the SPECIFIC product's entry in this step's
            // multiSelections map. Other selected products in the same step
            // are untouched.
            const stepMap = this.multiSelections.get(stepIndex);
            const sel = stepMap?.get(productId);
            if (sel) {
              sel.variantId = matched.id;
              sel.variantTitle = matched.title === 'Default Title' ? '' : matched.title;
              sel.price = matched.price;
              sel.image = matched.featuredImage || sel.image;
              this.updateUI();
            }
          } else {
            const sel = this.selections.get(stepIndex);
            if (sel) {
              sel.variantId = matched.id;
              sel.variantTitle = matched.title === 'Default Title' ? '' : matched.title;
              sel.price = matched.price;
              sel.image = matched.featuredImage || sel.image;
              this.selections.set(stepIndex, sel);
              this.updateUI();
            }
          }
        }
      }
    }

    /* Swap the card's 70px thumbnail to the picked variant's image. Uses the
       same URL widths as preloadGalleryImages so the swap reuses the warmed
       HTTP cache — no fetch round-trip on mobile variant picks. */
    _swapCardThumbnail(card, imageUrl) {
      const img = card.querySelector('.bba-row__media-img');
      if (!img || !imageUrl) return;
      img.src = this._resizeShopifyImageUrl(imageUrl, 200);
      img.srcset = [100, 150, 200]
        .map((w) => `${this._resizeShopifyImageUrl(imageUrl, w)} ${w}w`)
        .join(', ');
    }

    /* ---------- data lookup ---------- */
    findProduct(stepIndex, productId) {
      const step = this.steps[stepIndex];
      if (!step) return null;
      return step.products.find((p) => String(p.id) === String(productId));
    }

    findVariant(stepIndex, productId, variantId) {
      const product = this.findProduct(stepIndex, productId);
      if (!product) return null;
      return product.variants.find((v) => String(v.id) === String(variantId));
    }

    getStepLabel(stepIndex) {
      return this.steps[stepIndex]?.label || `Step ${stepIndex + 1}`;
    }

    getCardsInStep(stepIndex) {
      return this.querySelectorAll(`[data-bba-card][data-step-index="${stepIndex}"]`);
    }

    getSkipCardForStep(stepIndex) {
      return this.querySelector(`[data-bba-skip][data-step-index="${stepIndex}"]`);
    }

    getRequiredStepIndices() {
      return this.steps
        .map((s, i) => (s.required ? i : -1))
        .filter((i) => i >= 0)
        // Auto-skipped steps don't count as required (a bundle product covers them)
        .filter((i) => !this.autoSkipped.has(i));
    }

    /* ---------- multi-step helpers ----------
       Multi-select steps live in `this.multiSelections` (Map<stepIndex, Map<productId, sel>>)
       to keep the single-select code paths that read/write `this.selections`
       untouched. The helpers below abstract over BOTH stores so the rest of
       the class (updateSummary, updateTotal, submit, etc.) doesn't have to
       branch on step type for every read.
    */
    isMultiStep(stepIndex) {
      return !!(this.steps[stepIndex] && this.steps[stepIndex].multi);
    }

    /** True if the step has at least one product selected (or any item for multi). */
    hasStepSelection(stepIndex) {
      if (this.isMultiStep(stepIndex)) {
        const m = this.multiSelections.get(stepIndex);
        return !!(m && m.size > 0);
      }
      return this.selections.has(stepIndex);
    }

    /** Returns true if the user has picked ANY product in ANY step. */
    anyStepSelected() {
      if (this.selections.size > 0) return true;
      for (const m of this.multiSelections.values()) {
        if (m.size > 0) return true;
      }
      return false;
    }

    /** Flat array of every selection object (single + multi) — drives total, summary, submit. */
    getAllSelectionObjs() {
      const all = [];
      this.selections.forEach((sel) => all.push(sel));
      this.multiSelections.forEach((m) => m.forEach((sel) => all.push(sel)));
      return all;
    }

    /** Selection objects for a single step — for the summary's per-step row builder. */
    getStepSelectionObjs(stepIndex) {
      if (this.isMultiStep(stepIndex)) {
        const m = this.multiSelections.get(stepIndex);
        return m ? Array.from(m.values()) : [];
      }
      const s = this.selections.get(stepIndex);
      return s ? [s] : [];
    }

    /* ---------- UI update ---------- */
    updateUI() {
      this.updateAutoSkippedSteps();
      this.updateSummary();
      this.updateTotal();
      this.updateSubmitState();
      this.updateScrollSpy();
    }

    /* Toggle `.is-auto-skipped` on each step + show a banner naming the bundle product that covers it. */
    updateAutoSkippedSteps() {
      this.stepEls.forEach((stepEl) => {
        const idx = parseInt(stepEl.dataset.stepIndex, 10);
        const info = this.autoSkipped.get(idx);

        if (info) {
          stepEl.classList.add('is-auto-skipped');
          let banner = stepEl.querySelector('.bba-step__autoskip-banner');
          if (!banner) {
            banner = document.createElement('div');
            banner.className = 'bba-step__autoskip-banner';
            const list = stepEl.querySelector('.bba-step__list');
            if (list) list.insertAdjacentElement('beforebegin', banner);
            else stepEl.querySelector('.bba-step__options')?.appendChild(banner);
          }
          banner.textContent = `Đã bao gồm trong: ${info.byProductTitle}`;
        } else {
          stepEl.classList.remove('is-auto-skipped');
          stepEl.querySelector('.bba-step__autoskip-banner')?.remove();
        }
      });
    }

    /* Build the "Đơn của bạn" panel: one row per step. Shows selection details,
       "Bỏ qua" for skipped optional steps, "Chưa chọn" for pending required steps.
       Panel is always visible (it contains the total + submit button). */
    updateSummary() {
      if (!this.summaryList) return;

      // Inline factory for a single selection's <li> — same markup the old
      // single-step path used, lifted out so we can call it once per item
      // in a multi-step's selection map without duplicating the HTML.
      const renderSelRow = (sel) => {
        const categoryLine = sel.productCategory
          ? `<span class="bba-summary__category">${escapeHtml(sel.productCategory)}</span>`
          : '';
        const variantLine = sel.variantTitle
          ? `<span class="bba-summary__variant">${escapeHtml(sel.variantTitle)}</span>`
          : '';
        const priceText = (() => {
          try {
            return new Intl.NumberFormat(
              document.documentElement.lang || 'vi-VN',
              { maximumFractionDigits: 0 }
            ).format((sel.price * sel.quantity) / 100);
          } catch (_) {
            return Math.round((sel.price * sel.quantity) / 100).toLocaleString();
          }
        })();
        const img = sel.image
          ? `<img class="bba-summary__media-img" src="${escapeHtml(sel.image)}" alt="" loading="lazy">`
          : '';
        return `
          <li class="bba-summary__item">
            <span class="bba-summary__media">${img}</span>
            <span class="bba-summary__body">
              ${categoryLine}
              <span class="bba-summary__product">${escapeHtml(sel.productTitle || '')}</span>
              ${variantLine}
            </span>
            <span class="bba-summary__price">${priceText}</span>
          </li>
        `;
      };

      const rows = (this.steps || []).map((step, idx) => {
        // Auto-skipped step (covered by a bundle product) → omit; the bundle product's row
        // already represents the bundled value in the summary
        if (this.autoSkipped.has(idx)) return '';

        const isSkipped = this.skipped.has(idx);
        const stepLabel = step.label || `Bước ${idx + 1}`;
        const sels = this.getStepSelectionObjs(idx);

        if (sels.length > 0) {
          // Multi step renders one row per pick; single step always has 1.
          return sels.map(renderSelRow).join('');
        }

        // Skipped optional step → omit from summary (user explicitly opted out, no need to remind)
        if (isSkipped) return '';

        if (step.required) {
          return `
            <li class="bba-summary__item bba-summary__item--pending">
              <span class="bba-summary__media bba-summary__media--placeholder" aria-hidden="true"></span>
              <span class="bba-summary__body">
                <span class="bba-summary__product">${escapeHtml(stepLabel)}</span>
                <span class="bba-summary__variant">Chưa chọn</span>
              </span>
              <span class="bba-summary__price">0</span>
            </li>
          `;
        }

        // Optional step, neither selected nor explicitly skipped → omit from summary
        return '';
      });

      this.summaryList.innerHTML = rows.join('');
    }

    updateTotal() {
      if (!this.totalEl) return;
      const subtotal = this.getAllSelectionObjs().reduce(
        (sum, s) => sum + s.price * s.quantity,
        0
      );
      if (subtotal <= 0) {
        this.totalEl.textContent = '0';
        return;
      }
      // No currency symbol — format as decimal only
      try {
        this.totalEl.textContent = new Intl.NumberFormat(
          document.documentElement.lang || 'vi-VN',
          { maximumFractionDigits: 0 }
        ).format(subtotal / 100);
      } catch (_) {
        this.totalEl.textContent = Math.round(subtotal / 100).toLocaleString();
      }
    }

    updateScrollSpy() {
      this.querySelectorAll('[data-bba-spy]').forEach((dot) => {
        const idx = parseInt(dot.dataset.stepIndex, 10);
        // Step is "complete" once user picks something OR explicitly skips.
        // hasStepSelection covers both single (`selections`) and multi
        // (`multiSelections`) stores.
        const done = this.hasStepSelection(idx) || this.skipped.has(idx);
        dot.classList.toggle('is-complete', done);
      });
    }

    updateSubmitState() {
      const requiredIdx = this.getRequiredStepIndices();
      const missingRequired = requiredIdx.filter((i) => !this.hasStepSelection(i));
      const anySelected = this.anyStepSelected();
      const isReady = missingRequired.length === 0 && anySelected;

      if (this.submitBtn) this.submitBtn.disabled = !isReady || this.isSubmitting;

      if (this.submitHint) {
        if (missingRequired.length > 0 && anySelected) {
          const missingLabels = missingRequired.map((i) => this.getStepLabel(i)).join(', ');
          this.submitHint.hidden = false;
          this.submitHint.textContent = `Còn thiếu: ${missingLabels}`;
        } else {
          this.submitHint.hidden = true;
        }
      }
    }


    /* ---------- submit ---------- */
    async submit() {
      if (this.isSubmitting) return;
      const requiredIdx = this.getRequiredStepIndices();
      const missing = requiredIdx.filter((i) => !this.hasStepSelection(i));
      if (missing.length > 0) {
        const stepEl = this.querySelector(`.bba-step[data-step-index="${missing[0]}"]`);
        if (stepEl) stepEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (!this.anyStepSelected()) return;

      this.isSubmitting = true;
      this.submitBtn?.classList.add('is-loading');
      this.submitBtn && (this.submitBtn.disabled = true);

      // getAllSelectionObjs flattens both stores so multi-step picks ride into
      // the cart alongside single-step picks under the same `_bundle` tag.
      const items = this.getAllSelectionObjs().map((sel) => ({
        id: sel.variantId,
        quantity: sel.quantity || 1,
        properties: {
          _bundle: this.bundleKey,
          _bundle_step: sel.stepLabel,
        },
      }));

      try {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || err.message || 'Cart add failed');
        }

        // Fetch updated cart so we can dispatch cart:change with the cart detail.
        // Theme's auto-discount.js listens to this event to auto-apply HPW_COUPONS.
        let updatedCart = null;
        try {
          const cartRes = await fetch(
            `${window.Shopify?.routes?.root || '/'}cart.js`,
            { credentials: 'same-origin' }
          );
          if (cartRes.ok) updatedCart = await cartRes.json();
        } catch (_) {}

        // Dispatch cart:change (with cart detail) — triggers theme's auto-discount evaluation
        document.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: { cart: updatedCart },
          })
        );
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        window.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));

        // Brief wait so auto-discount has time to POST /cart/update.js before drawer opens
        await new Promise((r) => setTimeout(r, 350));

        const drawer = document.querySelector('cart-drawer, cart-notification, cart-notification-drawer');
        if (drawer && typeof drawer.show === 'function') {
          drawer.show();
        } else if (drawer && typeof drawer.open === 'function') {
          drawer.open();
        } else {
          window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
        }
      } catch (err) {
        console.error('[mod-bundle-builder] submit failed', err);
        if (this.submitHint) {
          this.submitHint.hidden = false;
          this.submitHint.textContent = err.message || 'Không thêm được vào giỏ. Vui lòng thử lại.';
        }
      } finally {
        this.isSubmitting = false;
        this.submitBtn?.classList.remove('is-loading');
        this.updateSubmitState();
      }
    }
  }

  customElements.define('mod-bundle-builder', BundleBuilderApple);
})();
